import { Link } from '@inertiajs/react'
import { useState, type ReactElement, type ReactNode } from 'react'
import Sidebar from '@/widgets/sidebar/Sidebar'
import { type PageNode } from '@/widgets/sidebar/PageTree'
import { AiChatPanel } from '@/widgets/ai-chat/AiChatPanel'

interface WorkspaceLayoutProps {
  workspaceName: string
  workspaceSlug: string
  pages: PageNode[]
  activePageSlug?: string
  userRole?: 'admin' | 'editor' | 'viewer'
  children: ReactNode
}

export default function WorkspaceLayout({
  workspaceName,
  workspaceSlug,
  pages,
  activePageSlug,
  userRole,
  children,
}: WorkspaceLayoutProps) {
  const [aiChatOpen, setAiChatOpen] = useState(false)

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar
        workspaceName={workspaceName}
        workspaceSlug={workspaceSlug}
        pages={pages}
        activePageSlug={activePageSlug}
        userRole={userRole}
      />

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-16 items-center justify-between border-b border-gray-200 bg-white px-6">
          <Link href="/workspaces" className="text-lg font-bold text-gray-900">
            Doqubase
          </Link>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setAiChatOpen(prev => !prev)}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
            >
              Ask AI
            </button>
            <Link
              href="/session"
              method="delete"
              as="button"
              className="rounded-md bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-200"
            >
              Log out
            </Link>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>

      <AiChatPanel
        workspaceSlug={workspaceSlug}
        isOpen={aiChatOpen}
        onClose={() => setAiChatOpen(false)}
      />
    </div>
  )
}

/**
 * Persistent layout function — shared reference across all workspace pages.
 * Inertia keeps the layout mounted between navigations when the same function is used.
 */
export function withWorkspaceLayout(page: ReactElement) {
  const { workspace, sidebar_pages, page: pageData, user_role } = page.props as {
    workspace?: { name: string; slug: string }
    sidebar_pages?: PageNode[]
    page?: { slug: string; path?: string }
    user_role?: 'admin' | 'editor' | 'viewer'
  }

  return (
    <WorkspaceLayout
      workspaceName={workspace?.name ?? ''}
      workspaceSlug={workspace?.slug ?? ''}
      pages={sidebar_pages ?? []}
      activePageSlug={pageData?.slug ?? undefined}
      userRole={user_role}
    >
      {page}
    </WorkspaceLayout>
  )
}
